import React from "react";
import { Box, Card } from "@mui/material";
import SectionFrame from "../SectionFrame";
import { EditableText } from "../InlineEditable";
import EditableImage from "../EditableImage";
import HighlightIcon from "../HighlightIcon";

export default function HighlightsSection({
  form,
  active,
  device,
  onSelect,
  onCommit,
  onUploadImage,
  onRemoveImage,
  disabled,
}) {
  const isMobile = device === "mobile";
  const sectionAppearance = form.appearance?.highlights || {};
  const primary =
    sectionAppearance.accentColor || form.theme.primaryColor || "#F35A3A";
  const secondary =
    sectionAppearance.titleColor || form.theme.secondaryColor || "#19242F";
  const textColor =
    sectionAppearance.textColor || form.theme.mutedTextColor || "#5B5D73";
  const backgroundColor =
    sectionAppearance.backgroundColor || form.theme.surfaceColor || "#fff";
  const cardColor =
    sectionAppearance.surfaceColor || form.theme.surfaceColor || "#fff";
  const highlights = [0, 1, 2].map(
    (index) => form.highlights?.[index] || {}
  );

  return (
    <SectionFrame
      id="highlights"
      label="Por qué elegirnos"
      active={active}
      enabled={form.sections.showHighlights !== false}
      onSelect={onSelect}
      onToggle={(next) => onCommit("sections.showHighlights", next)}
    >
      <Box
        sx={{
          px: isMobile ? 2.5 : 5,
          pr: isMobile ? 2.5 : 15,
          py: isMobile ? "var(--wp-space-48)" : "var(--wp-space-64)",
          bgcolor: backgroundColor,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto" }}>
          <EditableText
            value={form.highlightsIntro.eyebrow}
            fallback="Por qué elegirnos"
            disabled={disabled}
            onSave={(value) => onCommit("highlightsIntro.eyebrow", value)}
            sx={{
              color: primary,
              fontWeight: 950,
              textTransform: "uppercase",
              letterSpacing: 1.2,
              fontSize: 13,
            }}
          />
          <Box sx={{ mt: 0.6 }}>
            <EditableText
              value={form.highlightsIntro.title}
              fallback="Una experiencia pensada para tus clientes"
              disabled={disabled}
              onSave={(value) => onCommit("highlightsIntro.title", value)}
              sx={{
                color: secondary,
                fontWeight: "var(--wp-segment-title-weight)",
                fontSize: isMobile ? "var(--wp-title-30)" : "var(--wp-title-40)",
                letterSpacing: "var(--wp-segment-letter-spacing)",
              }}
            />
          </Box>
          <Box sx={{ mt: 1 }}>
            <EditableText
              value={form.highlightsIntro.subtitle}
              fallback="Explicá en pocas palabras qué hace diferente a tu negocio."
              disabled={disabled}
              multiline
              onSave={(value) => onCommit("highlightsIntro.subtitle", value)}
              sx={{ color: textColor, lineHeight: 1.65 }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            mt: 4,
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(3,minmax(0,1fr))",
            gap: 2,
          }}
        >
          {highlights.map((item, index) => {
            const identifier = item.id || `diferencial-${index + 1}`;
            return (
              <Card
                key={identifier}
                data-highlight-id={identifier}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${
                    form.theme.dividerColor || "rgba(25,36,47,.10)"
                  }`,
                  bgcolor: cardColor,
                }}
              >
                <EditableImage
                  value={item.imageUrl}
                  label={`Imagen diferencial ${index + 1}`}
                  height={140}
                  disabled={disabled}
                  onUpload={(file) =>
                    onUploadImage(`highlights.${index}.imageUrl`, file, {
                      sectionKey: "highlights",
                      assetKey: `highlight-${index + 1}`,
                    })
                  }
                  onRemove={() =>
                    onRemoveImage(`highlights.${index}.imageUrl`)
                  }
                  sx={{ transform: "scale(var(--wp-segment-image-scale))", transformOrigin: "center" }}
                />

                {!item.imageUrl ? (
                  <Box
                    sx={{
                      mt: 1.5,
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      display: "grid",
                      placeItems: "center",
                      color: primary,
                      bgcolor: "rgba(25,36,47,.05)",
                      border: "1px solid",
                      borderColor:
                        form.theme.dividerColor || "rgba(25,36,47,.10)",
                    }}
                  >
                    <HighlightIcon name={item.icon} sx={{ fontSize: 26 }} />
                  </Box>
                ) : null}

                <Box sx={{ mt: 2 }}>
                  <EditableText
                    value={item.title}
                    fallback={`Diferencial ${index + 1}`}
                    disabled={disabled}
                    onSave={(value) =>
                      onCommit(`highlights.${index}.title`, value)
                    }
                    sx={{ color: secondary, fontWeight: 950, fontSize: 19 }}
                  />
                </Box>
                <Box sx={{ mt: 0.8 }}>
                  <EditableText
                    value={item.description}
                    fallback="Contá el beneficio concreto que recibe el cliente."
                    disabled={disabled}
                    multiline
                    minRows={3}
                    onSave={(value) =>
                      onCommit(`highlights.${index}.description`, value)
                    }
                    sx={{ color: textColor, lineHeight: 1.6, fontSize: 14 }}
                  />
                </Box>
              </Card>
            );
          })}
        </Box>
      </Box>
    </SectionFrame>
  );
}
